import { AlterDatePill } from '@/components/admin/calendar/ManageScheduledLesson/AlterDatePill';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { IconWithText } from '@/components/ui/custom/dataDisplay/IconWithText';
import Text from '@/components/ui/custom/dataDisplay/Text';
import Skeleton from '@/components/ui/custom/Skeleton';
import { ActivityColorIcon, LoadingIcon } from '@/components/icons';
import { REQUEST_STALE_TIME_IN_MS } from '@/constants';
import {
  klassesQueryKeys,
  scheduledLessonsQueryKeys,
} from '@/data/services/querykeys';
import { formatLessonName } from '@/utils/lesson/name';
import { ROUTES } from '@/utils/routes';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { DetailTargetProps } from '../../card/NotificationTypes';
import { useTranslation } from 'react-i18next';
import { twJoin } from 'tailwind-merge';
import { bodyText } from '@/styles/styles';
import IconButton from '@/components/ui/custom/buttons/IconButton';

const DelayNotificationDetail: React.FC<DetailTargetProps<number>> = ({
  target: scheduledLessonId,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'alerts.detail.delay',
  });
  const { data: scheduledLesson, isInitialLoading } = useQuery(
    scheduledLessonsQueryKeys.get(scheduledLessonId || 0),
  );

  const { data: klass, isInitialLoading: isInitialLoadingKlass } = useQuery({
    ...klassesQueryKeys.get(scheduledLesson?.klass || 0),
    staleTime: REQUEST_STALE_TIME_IN_MS,
    enabled: !isNaN(scheduledLesson?.klass || NaN),
  });

  if (isInitialLoading) {
    return (
      <div className="w-full flex justify-center">
        <LoadingIcon className="h-5 w-5 text-primary" />
      </div>
    );
  }

  if (!scheduledLesson) {
    return <Text text={t('error')} />;
  }

  const { lesson, datetime } = scheduledLesson;

  const reportLink = ROUTES.CLASS.LESSON_REPORT(
    scheduledLesson.klass,
    scheduledLesson.id,
  );

  if (!klass) {
    return null;
  }

  const klassLink = ROUTES.ADMIN.CLASSES.INFO({
    name: klass?.name.replace('#', '') || '',
  });

  return (
    <article className="flex flex-wrap gap-0.5 justify-between bg-secondary-content p-1.5 rounded-md border border-secondary/20">
      <section className="flex flex-col gap-0.5">
        <section className="flex flex-col gap-0.5">
          <Link to={reportLink} className="flex justify-start">
            <IconWithText
              className={{
                base: 'flex-row-reverse gap-1',
                text: 'font-600 text-[1rem] underline text-primary hover:text-primary/40 transition-all duration-150 ease-in-out',
              }}
              text={formatLessonName(lesson)}
              icon={
                <IconButton
                  className={twJoin(
                    'p-0.5 bg-base-100 rounded-full border border-primary hover:ring-3 ring-primary-content hover:bg-primary-content disabled:cursor-not-allowed transition ease-in-out duration-200 transform hover:scale-105',
                  )}
                  icon={<ActivityColorIcon className="w-5 h-5" />}
                />
              }
            />
          </Link>
        </section>
        <section className="flex gap-1 w-full justify-between flex-wrap">
          <ConditionalRenderer
            condition={!isInitialLoadingKlass}
            fallback={
              <Skeleton className="bg-secondary/20 h-[15px] w-full rounded-md" />
            }
          >
            <Link to={klassLink}>
              <p
                className={twJoin(
                  bodyText,
                  'text-primary font-500 text-[0.875rem] md:text-[1rem] underline',
                  'hover:text-primary/40',
                  'transition-all duration-150 ease-in-out',
                )}
              >
                {klass?.name}
              </p>
            </Link>
          </ConditionalRenderer>
        </section>
      </section>
      <AlterDatePill
        dateTime={new Date(datetime)}
        klassDuration={klass?.duration}
      />
    </article>
  );
};

export default DelayNotificationDetail;
